import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import cx from "classnames";

import { createChart } from "krasulya-lightweight-charts";

import {
  USD_DECIMALS,
  SWAP,
  INCREASE,
  CHART_PERIODS,
  getTokenInfo,
  formatAmount,
  formatDateTime,
  usePrevious,
  getLiquidationPrice,
  getLiquidationPrice2,
  useLocalStorageSerializeKey,
  numberWithCommas,
  formatAmount2,
} from "../../helpers/Helpers";
import { useChartPrices } from "../../Api";
import Tab from "../Tab/Tab";
import up from 'src/assets/images/trade/updown.svg'
import down from 'src/assets/images/trade/down.svg'
import { getTokens, getToken } from "../../configs/Tokens";
import ChartTokenSelector from "./ChartTokenSelector";
import Row from "../Row";
import Tooltips from "../Tooltips/totalTips";
import { InterestProgress } from "./InterestProgress";

const PRICE_LINE_TEXT_WIDTH = 15;

const timezoneOffset = -new Date().getTimezoneOffset() * 60;

export function getChartToken(swapOption, fromToken, toToken, chainId) {
  if (!fromToken || !toToken) {
    return;
  }

  if (swapOption !== SWAP) {
    return toToken;
  }

  if (fromToken.isUsdx && toToken.isUsdx) {
    return getTokens(chainId).find((t) => t.isStable);
  }
  if (fromToken.isUsdx) {
    return toToken;
  }
  if (toToken.isUsdx) {
    return fromToken;
  }

  if (fromToken.isStable && toToken.isStable) {
    return toToken;
  }
  if (fromToken.isStable) {
    return toToken;
  }
  if (toToken.isStable) {
    return fromToken;
  }

  return toToken;
}

const DEFAULT_PERIOD = "4h";

const getSeriesOptions = () => ({
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/area-series.md
  lineColor: "#5472cc",
  topColor: "rgba(49, 69, 131, 0.4)",
  bottomColor: "rgba(42, 64, 103, 0.0)",
  lineWidth: 2,
  priceLineColor: "#3a3e5e",
  downColor: "#fa3c58",
  wickDownColor: "#fa3c58",
  upColor: "#0ecc83",
  wickUpColor: "#0ecc83",
  borderVisible: false,
});

const getChartOptions = (width, height, tokenDecimals) => ({
  width,
  height,
  layout: {
    backgroundColor: "rgba(255, 255, 255, 0)",
    textColor: "#ccc",
    fontFamily: "Relative",
  },
  localization: {
    // https://github.com/tradingview/lightweight-charts/blob/master/docs/customization.md#time-format
    timeFormatter: (businessDayOrTimestamp) => {
      return formatDateTime(businessDayOrTimestamp - timezoneOffset);
    },
    priceFormatter: (v) => {
      return formatAmount2(v, tokenDecimals, false);
    }
  },
  grid: {
    vertLines: {
      visible: true,
      color: "rgba(35, 38, 59, 1)",
      style: 2,
    },
    horzLines: {
      visible: true,
      color: "rgba(35, 38, 59, 1)",
      style: 2,
    },
  },
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/time-scale.md#time-scale
  timeScale: {
    rightOffset: 5,
    borderVisible: false,
    barSpacing: 5,
    timeVisible: true,
    fixLeftEdge: true,
  },
  // https://github.com/tradingview/lightweight-charts/blob/master/docs/customization.md#price-axis
  priceScale: {
    borderVisible: false,
  },
  crosshair: {
    horzLine: {
      color: "#aaa",
    },
    vertLine: {
      color: "#aaa",
    },
    mode: 0,
  },
});

export default function ExchangeTVChart(props) {
  const {
    elpName,
    swapOption,
    fromTokenAddress,
    toTokenAddress,
    tokenDecimals,
    infoTokens,
    chainId,
    positions,
    savedShouldShowPositionLines,
    orders,
    setToTokenAddress,
    setFromTokenAddress
  } = props;
  const [currentChart, setCurrentChart] = useState();
  const [currentSeries, setCurrentSeries] = useState();

  let [period, setPeriod] = useLocalStorageSerializeKey([chainId, "Chart-period"], DEFAULT_PERIOD);
  if (!(period in CHART_PERIODS)) {
    period = DEFAULT_PERIOD;
  }

  const [hoveredCandlestick, setHoveredCandlestick] = useState();

  const tokens = getTokens(chainId);
  const token1 = tokens.filter(token => token.isElp1)
  const token2 = tokens.filter(token => token.isElp2)

  const fromToken = getTokenInfo(infoTokens, fromTokenAddress);
  const toToken = getTokenInfo(infoTokens, toTokenAddress);
  const [chartToken, setChartToken] = useState({
    maxPrice: null,
    minPrice: null,
  });
  useEffect(() => {
    const tmp = getChartToken(swapOption, fromToken, toToken, chainId);
    // console.log("tmp", tmp)
    setChartToken(tmp);
  }, [swapOption, fromToken, toToken, chainId]);

  const symbol = chartToken ? (chartToken.isWrapped ? chartToken.baseSymbol : chartToken.symbol) : undefined;
  const marketName = chartToken ? symbol + "_USD" : undefined;
  const previousMarketName = usePrevious(marketName);

  const currentOrders = useMemo(() => {
    if (swapOption === SWAP || !chartToken) {
      return [];
    }

    if (orders.length > 0) {
      return orders?.filter((order) => {
        if (order.type === SWAP) {
          // we can't show non-stable to non-stable swap orders with existing charts
          // so to avoid users confusion we'll show only long/short orders
          return false;
        }

        const indexToken = getToken(chainId, order.indexToken);
        return order.indexToken === chartToken.address || (chartToken.isNative && indexToken.isWrapped);

      });
    } else {
      return [];
    }
  }, [orders, chartToken, swapOption, chainId]);

  const ref = useRef(null);
  const chartRef = useRef(null);
  const currentAveragePrice =
    chartToken.maxPrice && chartToken.minPrice ? chartToken.maxPrice.add(chartToken.minPrice).div(2) : null;
  const [priceData, updatePriceData] = useChartPrices(
    chainId,
    chartToken.symbol,
    chartToken.isStable,
    period,
    currentAveragePrice
  );

  const [chartInited, setChartInited] = useState(false);
  useEffect(() => {
    if (marketName !== previousMarketName) {
      setChartInited(false);
    }
  }, [marketName, previousMarketName]);

  const scaleChart = useCallback(() => {
    const from = Date.now() / 1000 - (7 * 24 * CHART_PERIODS[period]) / 2 + timezoneOffset;
    const to = Date.now() / 1000 + timezoneOffset;
    currentChart.timeScale().setVisibleRange({ from, to });
  }, [currentChart, period]);

  const onCrosshairMove = useCallback(
    (evt) => {
      if (!evt.time) {
        setHoveredCandlestick(null);
        return;
      }

      for (const point of evt.seriesPrices.values()) {
        setHoveredCandlestick((hoveredCandlestick) => {
          if (hoveredCandlestick && hoveredCandlestick.time === evt.time) {
            // rerender optimisations
            return hoveredCandlestick;
          }
          return {
            time: evt.time,
            ...point,
          };
        });
        break;
      }
    },
    [setHoveredCandlestick]
  );

  useEffect(() => {
    if (!ref.current || !priceData || !priceData.length || currentChart) {
      return;
    }

    const chart = createChart(
      chartRef.current,
      getChartOptions(chartRef.current.offsetWidth, chartRef.current.offsetHeight, tokenDecimals)
    );

    chart.subscribeCrosshairMove(onCrosshairMove);

    const series = chart.addCandlestickSeries(getSeriesOptions());
    setCurrentChart(chart);
    setCurrentSeries(series);
  }, [ref, priceData, currentChart, onCrosshairMove, tokenDecimals]);

  useEffect(() => {
    const interval = setInterval(() => {
      updatePriceData(undefined, true);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [updatePriceData]);

  useEffect(() => {
    if (!currentChart) {
      return;
    }
    const resizeChart = () => {
      currentChart.resize(chartRef.current.offsetWidth, chartRef.current.offsetHeight);
    };
    window.addEventListener("resize", resizeChart);
    return () => window.removeEventListener("resize", resizeChart);
  }, [currentChart]);

  useEffect(() => {
    if (currentSeries && priceData && priceData.length) {
      currentSeries.setData(priceData);
      if (!chartInited) {
        scaleChart();
        setChartInited(true);
      }
    }
  }, [priceData, currentSeries, chartInited, scaleChart]);

  useEffect(() => {
    const lines = [];
    if (currentSeries && savedShouldShowPositionLines) {
      if (currentOrders && currentOrders.length > 0) {
        currentOrders.forEach((order) => {
          const indexToken = getToken(chainId, order.indexToken);
          let tokenSymbol;
          if (indexToken && indexToken.symbol) {
            tokenSymbol = indexToken.isWrapped ? indexToken.baseSymbol : indexToken.symbol;
          }
          const title = `${order.type === INCREASE ? "Inc." : "Dec."} ${tokenSymbol} ${order.isLong ? "Long" : "Short"
            }`;
          const color = "#3a3e5e";
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(order.triggerPrice, USD_DECIMALS, tokenDecimals)),
              color,
              title: title.padEnd(PRICE_LINE_TEXT_WIDTH, " "),
            })
          );
        });
      }
      if (positions && positions.length > 0) {
        const color = "#3a3e5e";

        positions.forEach((position) => {
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(position.averagePrice, USD_DECIMALS, tokenDecimals)),
              color,
              title: `Open ${position.indexTokenInfo.symbol} ${position.isLong ? "Long" : "Short"}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ),
            })
          );
          // const liquidationPrice = getLiquidationPrice(position);
          const liquidationPrice = getLiquidationPrice2(position);
          lines.push(
            currentSeries.createPriceLine({
              price: parseFloat(formatAmount(liquidationPrice, USD_DECIMALS, tokenDecimals)),
              color,
              title: `Liq. ${position.indexTokenInfo.symbol} ${position.isLong ? "Long" : "Short"}`.padEnd(
                PRICE_LINE_TEXT_WIDTH,
                " "
              ),
            })
          );
        });
      }
    }
    return () => {
      lines.forEach((line) => currentSeries.removePriceLine(line));
    };
  }, [currentOrders, positions, currentSeries, chainId, savedShouldShowPositionLines, tokenDecimals]);

  const candleStatsHtml = useMemo(() => {
    if (!priceData) {
      return null;
    }
    const candlestick = hoveredCandlestick || priceData[priceData.length - 1];
    if (!candlestick) {
      return null;
    }

    const className = cx({
      "ExchangeChart-bottom-stats": true,
      positive: candlestick.open <= candlestick.close,
      negative: candlestick.open > candlestick.close,
      [`length-${String(parseInt(candlestick.close)).length}`]: true,
    });

    return (
      <div className={className}>
        <span className="ExchangeChart-bottom-stats-label">O</span>
        <span className="ExchangeChart-bottom-stats-value">{candlestick.open.toFixed(tokenDecimals)}</span>
        <span className="ExchangeChart-bottom-stats-label">H</span>
        <span className="ExchangeChart-bottom-stats-value">{candlestick.high.toFixed(tokenDecimals)}</span>
        <span className="ExchangeChart-bottom-stats-label">L</span>
        <span className="ExchangeChart-bottom-stats-value">{candlestick.low.toFixed(tokenDecimals)}</span>
        <span className="ExchangeChart-bottom-stats-label">C</span>
        <span className="ExchangeChart-bottom-stats-value">{candlestick.close.toFixed(tokenDecimals)}</span>
      </div>
    );
  }, [hoveredCandlestick, priceData]);

  let high;
  let low;
  let deltaPrice;
  let delta;
  let deltaPercentage;
  let deltaPercentageStr;

  const now = parseInt(Date.now() / 1000);
  const timeThreshold = now - 24 * 60 * 60;

  if (priceData) {
    // console.log("priceData::",priceData);
    for (let i = priceData.length - 1; i > 0; i--) {
      const price = priceData[i];
      if (price.time < timeThreshold) {
        break;
      }
      if (!low) {
        low = price.low;
      }
      if (!high) {
        high = price.high;
      }

      if (price.high > high) {
        high = price.high;
      }
      if (price.low < low) {
        low = price.low;
      }
      deltaPrice = price.open;
    }
  }
  if (deltaPrice && currentAveragePrice) {
    const average = parseFloat(formatAmount(currentAveragePrice, USD_DECIMALS, tokenDecimals));
    delta = average - deltaPrice;
    deltaPercentage = (delta * 100) / average;
    if (deltaPercentage > 0) {
      deltaPercentageStr = `+${deltaPercentage.toFixed(tokenDecimals)}%`;
    } else {
      deltaPercentageStr = `${deltaPercentage.toFixed(tokenDecimals)}%`;
    }
    if (deltaPercentage === 0) {
      deltaPercentageStr = "0.00";
    }
  }

  if (!chartToken) {
    return null;
  }

  const onSelectToken = (token) => {
    const tmp = getTokenInfo(infoTokens, token.address);
    setChartToken(tmp);
    setToTokenAddress(swapOption, token.address);
  };

  return (
    <div className="ExchangeChart tv" ref={ref}>
      <div className="ExchangeChart-top App-box App-box-border">
        <div className="ExchangeChart-top-inner">
          <div className="tratad_top">
            <div className="ExchangeChart-title">
              <ChartTokenSelector
                elpName={elpName}
                fromTokenAddress={fromTokenAddress}
                chainId={chainId}
                selectedToken={chartToken}
                swapOption={swapOption}
                infoTokens={infoTokens}
                onSelectToken={onSelectToken}
                className="chart-token-selector"
              />
            </div>
            <div className="price_ad">
              <div>

                <div className={cx({ positive: deltaPercentage > 0, negative: deltaPercentage < 0 }, 'ExchangeChart-main-price')}>
                  {chartToken.maxPrice && formatAmount(chartToken.maxPrice, USD_DECIMALS, tokenDecimals, true)}
                  {/* {deltaPercentage > 0 ?
                  < img src={up} />
                  :
                  <img src={down} />
                } */}
                </div>
                {/* <div className={"ExchangeChart-info-label mt-6"}>
                  ${chartToken.minPrice && formatAmount(chartToken.minPrice, USD_DECIMALS, tokenDecimals, true)}
                </div> */}
                <div className={cx({ positive: deltaPercentage > 0, negative: deltaPercentage < 0 }, 'upliver mt-4')}>
                  {!deltaPercentageStr && "-"}
                  {deltaPercentageStr && deltaPercentageStr}
                </div>
              </div>
            </div>
          </div>
          <div className="tratad_top tratad_toph5">
            {/* <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">Open Interest</div>
              <div className="mt-8 color4">
                <Row justify="space-between">
                  <div className="color18">$1.3M</div>
                  <div className="color24">$1.3M</div>
                </Row>
                <InterestProgress disabled
                  aria-label="Always visible"
                  valueLabelDisplay="none"
                  defaultValue={90} />
              </div>
            </div>
            <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">
                <Tooltips
                  title={
                    <React.Fragment>
                      <div className="">

                      </div>
                    </React.Fragment>
                  }
                >
                  <span className="toolBottom">Funding Rate</span>
                </Tooltips>
              </div>
              <Row align="center" className="mt-16">
                <Row className="">
                  <div className="color18 font-weight-b">L</div>
                  <div className="ml-4">0.0048%</div>
                </Row>
                <Row className="ml-16">
                  <div className="color24 font-weight-b">L</div>
                  <div className="ml-4">0.0048%</div>
                </Row>
              </Row>
            </div>
            <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">
                <Tooltips
                  title={
                    <React.Fragment>
                      <div className="">

                      </div>
                    </React.Fragment>
                  }
                >
                  <span className="toolBottom">Premium</span>
                </Tooltips>
              </div>
              <Row align="center" className="mt-16">
                <Row className="">
                  <div className="color18 font-weight-b">L</div>
                  <div className="ml-4">0.0048%</div>
                </Row>
                <Row className="ml-16">
                  <div className="color24 font-weight-b">L</div>
                  <div className="ml-4">0.0048%</div>
                </Row>
              </Row>
            </div> */}
            <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">24h High</div>
              <div className="mt-16 color4">
                {!high && "-"}
                {high && numberWithCommas(high.toFixed(tokenDecimals))}
              </div>
            </div>
            <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">24h Low</div>
              <div className="mt-16 color4">
                {!low && "-"}
                {low && numberWithCommas(low.toFixed(tokenDecimals))}
              </div>
            </div>
            {/* <div className="ExchangeChart-additional-info h5info">
              <div className="ExchangeChart-info-label">24h Vol.(USD)</div>
              <div className="mt-16 color4">
                {!low && "-"}
                {low && numberWithCommas(low.toFixed(tokenDecimals))}
              </div>
            </div> */}
          </div>
        </div>
      </div>
      <div className="ExchangeChart-bottom App-box App-box-border">
        <div className="ExchangeChart-bottom-header">
          <div className="ExchangeChart-bottom-controls">
            <Tab options={Object.keys(CHART_PERIODS)} option={period} setOption={setPeriod} />
          </div>
          {candleStatsHtml}
        </div>
        <div className="ExchangeChart-bottom-content" ref={chartRef}></div>
      </div>
    </div>
  );
}
